import React, { useEffect, useState } from 'react';
import router from 'next/router';
import { useMediaQuery, useTheme } from '@mui/material';

// Components
import Breadcrumb from '../breadcrumb/Breadcrumb';
import Markdown from '../markdown/Markdown';

// Utils
import { getStrapiMedia } from '../../utils/media';
import { capitalizeOnlyFirstLetter } from 'corporate-utils';

// Models
import {
  HeaderCoverResizeType,
  HeaderDescriptionPositionType,
} from '../../models/enum';
import {
  Button,
  ButtonWrapper,
  ContentDivision,
  CoverContainer,
  Description,
  ImageContainerDefaultStyled,
  ImageContainerStyled,
  OverlayStyled,
  StyledVideo,
  TextWrapper,
  Title,
  Wrapper,
} from './BannerVideo.style';
import { Media } from '../../models/auto-generated-strapi-types/types/Media';

export interface BannerVideoProps {
  title?: string;
  description?: string;
  titleFontSize?: number;
  descriptionFontSize?: number;
  titleMobileFontSize?: number;
  descriptionMobileFontSize?: number;
  titleFontWeight?: number;
  titleMobileFontWeight?: number;
  descriptionFontWeight?: number;
  descriptionMobileFontWeight?: number;
  video?: { data: Media };
  videoAutoPlay?: boolean;
  videoLoop?: boolean;
  videoMuted?: boolean;
  videoControls?: boolean;
  button?: {
    text?: string;
    textFontSize?: number;
    textMobileFontSize?: number;
    textMobileFontWeight?: number;
    textFontWeight?: number;
    url?: string;
    type?: 'primary' | 'secondary';
  };
  overlayColor?: string;
  breadCrumb?: {
    links: {
      href: string;
      name: string;
      disabled?: boolean;
      color?: string;
    }[];
    pageActive: {
      name: string;
      color?: string;
    };
  };
  className?: string;
  fixedHeight?: boolean;
  id?: string | number;
  textLineLimit?: boolean;
  fullContentHeight?: boolean;
  forceHideDescription?: boolean;
  forceHideButton?: boolean;
  forceHideBreadcrumb?: boolean;
  headerDescriptionPosition?: 'default' | 'under' | 'hidden';
  forceHideTitle?: boolean;
  forceDescriptionToBeWhite?: boolean;
  forceDescriptionToBeBlack?: boolean;
  headerCoverResize?: 'default_cover' | 'contain' | 'hidden';
}

const Banner: React.FC<BannerVideoProps> = ({
  breadCrumb,
  title,
  description,
  descriptionFontSize,
  descriptionMobileFontSize,
  descriptionFontWeight,
  descriptionMobileFontWeight,
  button,
  video,
  videoAutoPlay,
  videoControls,
  videoMuted,
  videoLoop,
  overlayColor,
  id,
  textLineLimit = false,
  titleFontSize,
  titleMobileFontSize,
  titleFontWeight,
  titleMobileFontWeight,
  forceDescriptionToBeBlack,
  forceDescriptionToBeWhite,
  forceHideTitle,
  forceHideDescription,
  forceHideButton,
  forceHideBreadcrumb,
  headerDescriptionPosition = 'default',
  headerCoverResize = 'default_cover',
}: BannerVideoProps) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpointsMediaQuery.up('sm'));
  const isMobile = useMediaQuery(theme.breakpointsMediaQuery.up('sm'));
  const [, setRerender] = useState<number>(0); // Trick to force re-render

  useEffect(() => {
    setRerender(Date.now());
  }, []);

  headerCoverResize = headerCoverResize || 'default_cover';
  headerDescriptionPosition = headerDescriptionPosition || 'default';

  const descriptionId = `bannerDescription__${id}`;

  const atLeastSomethingInDescriptionIsTrue = () => {
    return (
      !forceHideButton ||
      !forceHideDescription ||
      !forceHideTitle ||
      !forceHideBreadcrumb
    );
  };

  return (
    <>
      {/* Image and description */}
      <CoverContainer headerCoverResize={headerCoverResize}>
        {/* COVER IMAGE */}
        {/* When image is cover */}
        <ImageContainerDefaultStyled headerCoverResize={headerCoverResize}>
          {video &&
            video.data &&
            video.data.attributes &&
            (HeaderCoverResizeType.DEFAULT_COVER ===
              (headerCoverResize as HeaderCoverResizeType) ||
              !headerCoverResize) && (
            <StyledVideo
              autoPlay={videoAutoPlay || false}
              controls={videoControls || false}
              loop={videoLoop || false}
              muted={videoMuted || false}
              objectFit={'cover'}
            >
              <source src={getStrapiMedia(video.data?.attributes?.url)} />
            </StyledVideo>
          )}
        </ImageContainerDefaultStyled>

        {/* COVER IMAGE */}
        {/* When image is contain */}
        <ImageContainerStyled>
          {video &&
            video?.data &&
            video?.data?.attributes &&
            (HeaderCoverResizeType.CONTAIN ===
              (headerCoverResize as HeaderCoverResizeType) ||
              !headerCoverResize) && (
            <StyledVideo
              autoPlay={videoAutoPlay || false}
              controls={videoControls || false}
              loop={videoLoop || false}
              muted={videoMuted || false}
              objectFit={'contain'}
            >
              <source src={getStrapiMedia(video.data?.attributes?.url)} />
            </StyledVideo>
          )}
        </ImageContainerStyled>

        {/* Overlay cover */}
        {(typeof window === 'undefined' || isDesktop) && overlayColor ? (
          <OverlayStyled overlay={overlayColor}></OverlayStyled>
        ) : (
          <></>
        )}

        {/* Description inside image */}
        {HeaderDescriptionPositionType.DEFAULT ===
          (headerDescriptionPosition as HeaderDescriptionPositionType) &&
        atLeastSomethingInDescriptionIsTrue() ? (
            <Wrapper lightContrast={true && !forceDescriptionToBeBlack}>
              <ContentDivision>
                {!forceHideBreadcrumb && breadCrumb && (
                  <Breadcrumb
                    {...breadCrumb}
                    lightContrast={
                      isMobile ? true : false || forceDescriptionToBeBlack
                    }
                  />
                )}
                {title && !forceHideTitle && (
                  <Title
                    fontSize={titleFontSize}
                    fontMobileSize={titleMobileFontSize}
                    fontWeight={titleFontWeight}
                    fontMobileWeight={titleMobileFontWeight}
                  >
                    <Markdown children={title}></Markdown>
                  </Title>
                )}
                {description && !forceHideDescription && (
                  <Description
                    textLineLimit={textLineLimit}
                    fontSize={descriptionFontSize}
                    fontMobileSize={descriptionMobileFontSize}
                    fontWeight={descriptionFontWeight}
                    fontMobileWeight={descriptionMobileFontWeight}
                  >
                    <Markdown id={descriptionId} children={description} />
                  </Description>
                )}
                {button && button.url && button.text && !forceHideButton && (
                  <ButtonWrapper>
                    <Button
                      onClick={(e: { preventDefault: () => void }): void => {
                        e.preventDefault();
                        button?.url && router.push(button?.url);
                      }}
                      type={button?.type || 'primary'}
                      fontSize={button?.textFontSize}
                      fontMobileSize={button?.textMobileFontSize}
                      fontWeight={button?.textFontWeight}
                      fontMobileWeight={button?.textMobileFontWeight}
                    >
                      {capitalizeOnlyFirstLetter(button.text)}
                    </Button>
                  </ButtonWrapper>
                )}
              </ContentDivision>
            </Wrapper>
          ) : (
            <></>
          )}
      </CoverContainer>

      {/* Description under the image */}
      {HeaderDescriptionPositionType.UNDER ===
        (headerDescriptionPosition as HeaderDescriptionPositionType) &&
      atLeastSomethingInDescriptionIsTrue() ? (
          <TextWrapper lightContrast={false || forceDescriptionToBeWhite}>
            <ContentDivision maxDimension={true}>
              {!forceHideBreadcrumb && breadCrumb && (
                <Breadcrumb
                  {...breadCrumb}
                  lightContrast={true && !forceDescriptionToBeWhite}
                />
              )}
              {HeaderDescriptionPositionType.UNDER ===
            (headerDescriptionPosition as HeaderDescriptionPositionType) ? (
                  <>
                    {title && !forceHideTitle && (
                      <Title
                        fontSize={titleFontSize}
                        fontMobileSize={titleMobileFontSize}
                        fontWeight={titleFontWeight}
                        fontMobileWeight={titleMobileFontWeight}
                      >
                        <Markdown children={title}></Markdown>
                      </Title>
                    )}
                    {description && !forceHideDescription && (
                      <Description
                        textLineLimit={textLineLimit}
                        fontSize={descriptionFontSize}
                        fontMobileSize={descriptionMobileFontSize}
                        fontWeight={descriptionFontWeight}
                        fontMobileWeight={descriptionMobileFontWeight}
                      >
                        <Markdown id={descriptionId} children={description} />
                      </Description>
                    )}
                    {button && button.url && button.text && !forceHideButton && (
                      <ButtonWrapper>
                        <Button
                          onClick={(e: { preventDefault: () => void }): void => {
                            e.preventDefault();
                            button?.url && router.push(button?.url);
                          }}
                          type={button?.type || 'primary'}
                          fontSize={button?.textFontSize}
                          fontMobileSize={button?.textMobileFontSize}
                          fontWeight={button?.textFontWeight}
                          fontMobileWeight={button?.textMobileFontWeight}
                        >
                          {capitalizeOnlyFirstLetter(button.text)}
                        </Button>
                      </ButtonWrapper>
                    )}
                  </>
                ) : (
                  <></>
                )}
            </ContentDivision>
          </TextWrapper>
        ) : (
          <></>
        )}
    </>
  );
};

export default Banner;
